import Link from "next/link";
import React, { useCallback, useState, useEffect } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Link as MuiLink,
  Tooltip,
  Typography
} from "@material-ui/core";
import { PhotoSizeSelectActual as PhotoSizeSelectActualIcon } from "@material-ui/icons/";
import { Alert } from "@material-ui/lab";
import {
  ActiveStatus,
  CollapsiblePanel,
  ShowValue,
  SubHeader,
  TableButton
} from "src/apps/commons/dataDisplays";
import {
  SimpleSubTable,
  SimpleSubTablePlaceholder
} from "src/apps/commons/form";
import { ListModalState } from "src/apps/commons/form/hooks";
import { ImageAvatar } from "src/apps/commons/fileUploads";
import { formatDate, formatDateTime } from "src/utils";
import { warrantyTypeMap } from "src/utils/enum";
import { IRemyndBrandBrief } from "../../remyndBrand/types";
import { IRemyndWarrantyDetail } from "../../remyndWarranty/types";
import { paths as remyndWarrantyPaths } from "../../remyndWarranty/utils";
import { IRemyndProduct } from "../../remyndProduct/types";
import {
  IRemyndVerifiedUserProductWarrantySaveReq,
  IUserProductBrief,
  IUserProductWarrantyBrief
} from "../types";
import { RemyndWarrantyListState } from "./Edit";
import { createStyles, makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(theme =>
  createStyles({
    redBoldText: {
      color: "#ff0000",
      fontWeight: theme.typography.fontWeightBold
    }
  })
);

interface Props {
  userProduct: IUserProductBrief;
  remyndBrand: IDropdown<IRemyndBrandBrief> | null;
  remyndProduct: IDropdown<IRemyndProduct> | null;
  remyndWarrantyListState: RemyndWarrantyListState;
  userWarrantyList: IUserProductWarrantyBrief[];
  savedWarrantyList: IRemyndVerifiedUserProductWarrantySaveReq[];
  addItem: (value: IRemyndVerifiedUserProductWarrantySaveReq) => void;
  delItem: (index: number) => void;
  moveItemUp: (index: number) => void;
  moveItemDown: (index: number) => void;
  setModal: (
    state: ListModalState<IRemyndVerifiedUserProductWarrantySaveReq>
  ) => void;
  setWarrantyNotify: (notify: boolean) => void;
  setWarrantyOverride: (override: boolean) => void;
  warrantyNotify: boolean;
  warrantyOverride: boolean;
  disabledMessage?: string;
}

const UserProductWarranty: React.FC<Props> = props => {
  const classes = useStyles();
  const {
    userProduct,
    remyndBrand,
    remyndProduct,
    remyndWarrantyListState,
    userWarrantyList,
    savedWarrantyList,
    addItem,
    delItem,
    moveItemUp,
    moveItemDown,
    setModal,
    setWarrantyNotify,
    setWarrantyOverride,
    warrantyNotify,
    warrantyOverride,
    disabledMessage
  } = props;

  const [usedMasterIds, setUsedMasterIds] = useState<RemyndWarrantyDetailId[]>(
    []
  );

  useEffect(() => {
    if (savedWarrantyList) {
      const refIds = savedWarrantyList
        .map(d => d.remyndWarrantyDetailId)
        .filter(id => Boolean(id)) as RemyndWarrantyDetailId[];
      setUsedMasterIds(refIds);
    }
  }, [savedWarrantyList]);

  const handleCopyUserWarranty = useCallback(
    (data: IUserProductWarrantyBrief) => {
      addItem({
        id: undefined,
        remyndWarrantyDetailId: undefined,
        ord: 0, // will be set at final submit
        active: true,
        startDate: data.startDate,
        endDate: data.endDate,
        labelTh: undefined,
        labelEn: undefined,
        type: data.type,
        years: data.years || 0,
        months: data.months || 0,
        days: data.days || 0,
        remarkTh: undefined,
        remarkEn: undefined,
        fileId: undefined,
        snapLabelTh: undefined,
        snapLabelEn: undefined,
        snapType: undefined,
        snapYears: undefined,
        snapMonths: undefined,
        snapDays: undefined,
        snapRemarkTh: undefined,
        snapRemarkEn: undefined,
        snapEffectiveFrom: undefined,
        snapEffectiveUntil: undefined
      });
    },
    [addItem]
  );

  const UserWarrantyManageCell = useCallback(
    (data: IUserProductWarrantyBrief) => (
      <TableButton type="copy" onClick={() => handleCopyUserWarranty(data)} />
    ),
    [handleCopyUserWarranty]
  );

  const handleAddRemyndWarranty = useCallback(
    (data: IRemyndWarrantyDetail) => {
      addItem({
        id: undefined,
        remyndWarrantyDetailId: data.id,
        ord: 0, // will be set at final submit
        active: true,
        startDate: undefined,
        endDate: undefined,
        labelTh: data.labelTh,
        labelEn: data.labelEn,
        type: data.type,
        years: data.years,
        months: data.months,
        days: data.days,
        remarkTh: data.remarkTh,
        remarkEn: data.remarkEn,
        fileId: undefined,
        snapLabelTh: data.labelTh,
        snapLabelEn: data.labelEn,
        snapType: data.type,
        snapYears: data.years,
        snapMonths: data.months,
        snapDays: data.days,
        snapRemarkTh: data.remarkTh,
        snapRemarkEn: data.remarkEn,
        snapEffectiveFrom: data.effectiveFrom,
        snapEffectiveUntil: data.effectiveUntil
      });

      setUsedMasterIds(oldIds => [...oldIds, data.id]);
    },
    [addItem, setUsedMasterIds]
  );

  const RemyndWarrantyManageCell = useCallback(
    (data: IRemyndWarrantyDetail) => {
      const added = usedMasterIds.includes(data.id);
      return (
        <TableButton
          type={added ? "check" : "add"}
          onClick={() => handleAddRemyndWarranty(data)}
          disabled={added}
        />
      );
    },
    [handleAddRemyndWarranty, usedMasterIds]
  );

  const NotificationCell = useCallback(
    (data: IUserProductWarrantyBrief) => (
      <ActiveStatus
        value={data.notification}
        tooltip={data.notification ? "แจ้งเตือน" : "ไม่แจ้งเตือน"}
      />
    ),
    []
  );

  const UserWarrantyImageCell = useCallback(
    (data: IUserProductWarrantyBrief) => {
      return data.imageId ? (
        <ImageAvatar fileId={data.imageId} />
      ) : (
        <Tooltip title="ไม่มีรูปภาพ" placement="top">
          <div>
            <IconButton disabled size="small">
              <PhotoSizeSelectActualIcon />
            </IconButton>
          </div>
        </Tooltip>
      );
    },
    []
  );

  const ReqWarrantyImageCell = useCallback(
    (data: IRemyndVerifiedUserProductWarrantySaveReq) => {
      return data.fileId ? (
        <ImageAvatar fileId={data.fileId} />
      ) : (
        <Tooltip title="ไม่มีรูปภาพ" placement="top">
          <div>
            <IconButton disabled size="small">
              <PhotoSizeSelectActualIcon />
            </IconButton>
          </div>
        </Tooltip>
      );
    },
    []
  );

  const ActiveCell = useCallback(
    (data: IRemyndVerifiedUserProductWarrantySaveReq) => (
      <ActiveStatus value={data.active} />
    ),
    []
  );

  const WarrantyDateCell = useCallback(
    (data: IRemyndVerifiedUserProductWarrantySaveReq) => {
      return data.type === "DURATION" ? (
        <div>
          {data.startDate ? (
            <span>{formatDate(data.startDate)}</span>
          ) : (
            <span className={classes.redBoldText}>ไม่ระบุ</span>
          )}
          {data.endDate ? (
            <span>{` - ${formatDate(data.endDate)}`}</span>
          ) : (
            <span className={classes.redBoldText}> - ไม่ระบุ</span>
          )}
        </div>
      ) : (
        <span>{""}</span>
      );
    },
    [classes.redBoldText]
  );

  return (
    <CollapsiblePanel title="Step 2. การรับประกัน" icon="edit">
      <Grid container spacing={2}>
        {disabledMessage && (
          <Grid item xs={12}>
            <Alert severity="error">{disabledMessage}</Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <SubHeader title="2.1. ข้อมูลการรับประกันที่ User ระบุ" />
        </Grid>
        <Grid item xs={12}>
          {userWarrantyList.length === 0 ? (
            <SimpleSubTablePlaceholder text="User ไม่ได้กรอกข้อมูลการรับประกัน" />
          ) : (
            <SimpleSubTable<IUserProductWarrantyBrief>
              data={userWarrantyList}
              showIndex={false}
              columns={[
                {
                  label: "จัดการ",
                  align: "center",
                  width: 92,
                  style: { padding: 0 },
                  renderCell: UserWarrantyManageCell
                },
                {
                  label: "ประเภทการรับประกัน",
                  width: 200,
                  renderCell: row => warrantyTypeMap[row.type]
                },
                {
                  label: "ระยะเวลารับประกัน",
                  minWidth: 180,
                  renderCell: row =>
                    row.type === "DURATION"
                      ? `${row.years || 0} ปี, ${row.months || 0} เดือน, ${
                          row.days || 0
                        } วัน`
                      : ""
                },
                {
                  label: "ช่วงเวลารับประกัน",
                  minWidth: 200,
                  renderCell: row =>
                    row.type === "DURATION"
                      ? `${
                          row.startDate
                            ? formatDate(row.startDate)
                            : "ไม่ระบุวันที่เริ่ม"
                        } - ${
                          row.endDate
                            ? formatDate(row.endDate)
                            : "ไม่ระบุวันที่สิ้นสุด"
                        }`
                      : ""
                },
                {
                  label: "แจ้งเตือน",
                  align: "center",
                  width: 100,
                  renderCell: NotificationCell
                },
                {
                  label: "รูปใบรับประกัน",
                  align: "center",
                  width: 160,
                  style: { padding: 0 },
                  renderCell: UserWarrantyImageCell
                }
              ]}
            />
          )}
        </Grid>
        <Grid item xs={12}>
          <SubHeader title="2.2. ข้อมูลการรับประกันจาก Master" marginTop />
        </Grid>
        <Grid item container xs={12}>
          {remyndWarrantyListState.fetching ? (
            <SimpleSubTablePlaceholder loading />
          ) : remyndWarrantyListState.list.length === 0 ? (
            <SimpleSubTablePlaceholder
              text={
                remyndProduct
                  ? `ไม่พบข้อมูล Master (ที่ Active) สำหรับสินค้าที่เลือก : ${remyndProduct.meta.nameTh}`
                  : remyndBrand
                  ? `ไม่พบข้อมูล Master (ที่ Active) สำหรับแบรนด์/หมวดหมู่ที่เลือก`
                  : `กรุณาเลือกแบรนด์และหมวดหมู่ หรือ เลือกสินค้า`
              }
            />
          ) : (
            remyndWarrantyListState.list.map(warrantyContainer => {
              const { details, warranty } = warrantyContainer;
              return (
                <Grid key={warranty.id} item container spacing={1} xs={12}>
                  <Grid item xs={6}>
                    <Box ml={1}>
                      <Typography variant="h6">{warranty.titleTh}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box ml={1}>
                      <Typography variant="h6">{warranty.titleEn}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box ml={1}>
                      <Typography variant="body2" color="textSecondary">
                        {warranty.remarkTh ?? "ไม่มีหมายเหตุ (TH)"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box ml={1}>
                      <Typography variant="body2" color="textSecondary">
                        {warranty.remarkEn ?? "ไม่มีหมายเหตุ (EN)"}
                      </Typography>
                    </Box>
                  </Grid>
                  {warranty.imageId && (
                    <Grid item xs={6}>
                      <Box ml={1}>
                        <ImageAvatar
                          renderAsLink
                          fileId={warranty.imageId}
                          linkTitle="แสดงรูปประกอบ"
                        />
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={warranty.imageId ? 6 : 12}>
                    <Box mr={1} textAlign="right">
                      <Link
                        passHref
                        href={`${remyndWarrantyPaths.edit}?id=${warranty.id}`}
                      >
                        <MuiLink target="_blank">
                          ไปที่หน้าข้อมูลการรับประกัน
                        </MuiLink>
                      </Link>
                    </Box>
                  </Grid>
                  <Grid item xs={12} />
                  <Grid item xs={12}>
                    <SimpleSubTable<IRemyndWarrantyDetail>
                      data={details}
                      showIndex={false}
                      readonly={!!disabledMessage}
                      columns={[
                        {
                          label: "จัดการ",
                          align: "center",
                          width: 92,
                          style: { padding: 0 },
                          renderCell: RemyndWarrantyManageCell
                        },
                        {
                          label: "#",
                          align: "center",
                          width: 60,
                          renderCell: (_, idx) => idx + 1
                        },
                        {
                          label: "ชื่อ (TH)",
                          minWidth: 140,
                          renderCell: row => row.labelTh
                        },
                        {
                          label: "ชื่อ (EN)",
                          minWidth: 140,
                          renderCell: row => row.labelEn
                        },
                        {
                          label: "หมายเหตุ (TH)",
                          minWidth: 140,
                          style: { whiteSpace: "pre-wrap" },
                          renderCell: row => row.remarkTh
                        },
                        {
                          label: "หมายเหตุ (EN)",
                          minWidth: 140,
                          style: { whiteSpace: "pre-wrap" },
                          renderCell: row => row.remarkEn
                        },
                        {
                          label: "ระยะเวลารับประกัน",
                          width: 180,
                          renderCell: row =>
                            row.type === "DURATION"
                              ? `${row.years} ปี, ${row.months} เดือน, ${row.days} วัน`
                              : "ตลอดอายุการใช้งาน"
                        },
                        {
                          label: "Effective Date",
                          width: 180,
                          renderCell: row =>
                            !row.effectiveFrom && !row.effectiveUntil
                              ? ""
                              : `${
                                  row.effectiveFrom
                                    ? formatDateTime(row.effectiveFrom)
                                    : "ไม่ระบุ"
                                } - ${
                                  row.effectiveUntil
                                    ? formatDateTime(row.effectiveUntil)
                                    : "ไม่ระบุ"
                                }`
                        }
                      ]}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box mb={2} />
                  </Grid>
                </Grid>
              );
            })
          )}
        </Grid>
        <Grid item xs={12}>
          <SubHeader
            title="2.3. ข้อมูลการรับประกันสำหรับ User Product นี้"
            marginTop
          />
        </Grid>{" "}
        <Grid item xs={6}>
          <ShowValue
            label="วันที่ซื้อ"
            value={
              userProduct.receipt?.purchasedDate &&
              formatDate(userProduct.receipt.purchasedDate)
            }
          />
          <ShowValue
            label="รูปใบเสร็จ"
            value={
              userProduct.receipt?.imageId && (
                <ImageAvatar
                  renderAsLink
                  fileId={userProduct.receipt?.imageId}
                />
              )
            }
          />
        </Grid>
        <Grid item xs={12}>
          <SimpleSubTable<IRemyndVerifiedUserProductWarrantySaveReq>
            data={savedWarrantyList}
            readonly={!!disabledMessage}
            onClickAdd={() => setModal({ open: true })}
            onClickEdit={(req, idx) =>
              setModal({
                open: true,
                index: idx,
                item: req
              })
            }
            onClickDelete={(req, idx) => {
              delItem(idx);
              setUsedMasterIds(oldIds =>
                oldIds.filter(id => id !== req.remyndWarrantyDetailId)
              );
            }}
            onMoveUp={moveItemUp}
            onMoveDown={moveItemDown}
            showIndex
            columns={[
              {
                label: "ชื่อ (TH)",
                minWidth: 140,
                renderCell: req => req.labelTh
              },
              {
                label: "ชื่อ (EN)",
                minWidth: 140,
                renderCell: req => req.labelEn
              },
              {
                label: "หมายเหตุ (TH)",
                minWidth: 140,
                style: { whiteSpace: "pre-wrap" },
                renderCell: req => req.remarkTh
              },
              {
                label: "หมายเหตุ (EN)",
                minWidth: 140,
                style: { whiteSpace: "pre-wrap" },
                renderCell: req => req.remarkEn
              },
              {
                label: "ระยะเวลารับประกัน",
                width: 180,
                renderCell: req =>
                  req.type === "DURATION"
                    ? `${req.years || 0} ปี, ${req.months || 0} เดือน, ${
                        req.days || 0
                      } วัน`
                    : "ตลอดอายุการใช้งาน"
              },
              {
                label: "ช่วงเวลาที่รับประกัน",
                align: "center",
                width: 230,
                renderCell: WarrantyDateCell
              },
              {
                label: "รูป",
                align: "center",
                width: 100,
                style: { padding: 0 },
                renderCell: ReqWarrantyImageCell
              },
              {
                label: "Active",
                align: "center",
                width: 80,
                renderCell: ActiveCell
              }
            ]}
          />
        </Grid>
        {!disabledMessage && (
          <>
            <Grid item xs={12}>
              {warrantyOverride ? (
                <Alert severity="success">
                  เมื่อกดบันทึก ทาง user
                  จะเห็นข้อมูลการรับประกันตามที่ระบุในตาราง 2.3. เท่านั้น
                  (เฉพาะที่ active) และถ้าไม่มีข้อมูล จะเห็นว่า
                  [สินค้านี้ไม่มีการรับประกัน]
                </Alert>
              ) : (
                <Alert severity="info">
                  ทาง user จะยังไม่เห็นข้อมูลส่วนนี้
                  ติ๊กเลือกข้างล่างหากต้องการนำข้อมูลในตาราง 2.3. ไปแสดงให้ user
                  เห็น
                </Alert>
              )}
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Box ml={1}>
                    <Checkbox
                      color="primary"
                      checked={warrantyOverride}
                      onChange={v => {
                        const override = v.target.checked;
                        setWarrantyOverride(override);
                        setWarrantyNotify(override); // default notify if override
                      }}
                    />
                  </Box>
                }
                label="นำข้อมูลการรับประกันนี้ไปแสดงให้ User เห็นในแอป Remynd Home"
                labelPlacement="end"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Box ml={1}>
                    <Checkbox
                      color="primary"
                      checked={warrantyNotify}
                      disabled={!warrantyOverride}
                      onChange={v => {
                        setWarrantyNotify(v.target.checked);
                      }}
                    />
                  </Box>
                }
                label="ส่ง Notification แจ้ง User ด้วย (ระบบจะส่งเฉพาะถ้ามีการแก้ไขข้อมูล)"
                labelPlacement="end"
              />
            </Grid>
          </>
        )}
      </Grid>
    </CollapsiblePanel>
  );
};

export default UserProductWarranty;
